// All pure scoring math + tuning constants. No DOM, no Maps, no network —
// fully unit-testable. Ported verbatim from the legacy single-file app, with
// `state.trips` threaded in as a parameter instead of read from a global.

import type {
  Amenity,
  AmenityGroup,
  Candidate,
  LatLng,
  ScoreClass,
  TravelMode,
  TravelRegion,
  Trip,
  TripLegData,
} from './types';

// ============ Geometry ============
export function haversineKm(a: LatLng, b: LatLng): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

/** Ray-casting point-in-polygon test (ring is a closed or open lat/lng loop). */
export function pointInPolygon(p: LatLng, ring: LatLng[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i].lng, yi = ring[i].lat;
    const xj = ring[j].lng, yj = ring[j].lat;
    const intersects =
      (yi > p.lat) !== (yj > p.lat) &&
      p.lng < ((xj - xi) * (p.lat - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

/** True when a point falls inside the active travel-time region. */
export function isInRegion(p: LatLng, region: TravelRegion): boolean {
  if (region.kind === 'circle') {
    return haversineKm(p, region.center) * 1000 <= region.radiusMeters;
  }
  return pointInPolygon(p, region.path);
}

// Speed constants used to convert Haversine km to per-mode minutes. These only
// power instant, offline previews (the radius circle + suggestion pre-ranking);
// real travel times always come from the routing APIs, so these are deliberately
// rough, mid-density-city defaults rather than tuned to any one place.
export const HEURISTIC_SPEEDS = {
  walkMinPerKm: 12,
  bikeMinPerKm: 4,
  transitMinPerKm: 3.5,
  driveMinPerKm: 2, // city w/ traffic
  tripMinPerKm: 1.8, // freeway-ish for regional drives
};

// ============ Commute (friends) ============
export const MODE_THRESHOLDS: Record<TravelMode, [number, number]> = {
  DRIVING: [20, 35],
  TRANSIT: [30, 50],
  BICYCLING: [25, 45],
  WALKING: [30, 60],
};

// Weights reflect what the user cares about most: walking & biking.
export const MODE_WEIGHTS: Record<TravelMode, number> = {
  WALKING: 0.32,
  BICYCLING: 0.32,
  TRANSIT: 0.18,
  DRIVING: 0.18,
};

export const MODE_COLORS: Record<TravelMode, string> = {
  WALKING: '#5a8a3a', // olive
  BICYCLING: '#b8861f', // amber
  TRANSIT: '#3d5a8e', // muted indigo
  DRIVING: '#c44536', // sienna
};

export const ORS_PROFILES: Partial<Record<TravelMode, string>> = {
  WALKING: 'foot-walking',
  BICYCLING: 'cycling-regular',
  DRIVING: 'driving-car',
  // TRANSIT intentionally absent — ORS has no transit profile.
};

export function timeClass(min: number, mode: TravelMode): ScoreClass {
  const thresh = MODE_THRESHOLDS[mode];
  if (min <= thresh[0]) return 'good';
  if (min <= thresh[1]) return 'mid';
  return 'bad';
}

export function modeScore(avgMinutes: number, mode: TravelMode): number {
  // Normalize a mode's avg into a 0-100 score using the same thresholds
  // we use for color-coding. <= good → 100, == mid → 50, >= 1.5*mid → 0.
  const [good, mid] = MODE_THRESHOLDS[mode];
  const bad = mid * 1.5;
  if (avgMinutes <= good) return 100;
  if (avgMinutes <= mid) return 100 - (50 * (avgMinutes - good)) / (mid - good);
  if (avgMinutes <= bad) return 50 - (50 * (avgMinutes - mid)) / (bad - mid);
  return 0;
}

export interface ModeSummary {
  avg: number;
  worst: number;
  avgClass: ScoreClass;
  worstClass: ScoreClass;
}

export function summarizeCandidate(
  c: Candidate,
  mode: TravelMode,
): ModeSummary | null {
  const r = c.results[mode];
  if (!r || !r.length) return null;
  const valid = r.filter((x) => x.minutes != null);
  if (!valid.length) return null;
  const avg = Math.round(
    valid.reduce((s, x) => s + (x.minutes as number), 0) / valid.length,
  );
  const worst = Math.max(...valid.map((x) => x.minutes as number));
  return {
    avg,
    worst,
    avgClass: timeClass(avg, mode),
    worstClass: timeClass(worst, mode),
  };
}

export interface BlendedCommute {
  score: number;
  modeAvgs: Partial<Record<TravelMode, number>>;
}

export function blendedScore(candidate: Candidate): BlendedCommute | null {
  let total = 0;
  let weightSum = 0;
  const modeAvgs: Partial<Record<TravelMode, number>> = {};
  for (const [mode, weight] of Object.entries(MODE_WEIGHTS) as [
    TravelMode,
    number,
  ][]) {
    const summary = summarizeCandidate(candidate, mode);
    if (!summary) return null;
    modeAvgs[mode] = summary.avg;
    total += modeScore(summary.avg, mode) * weight;
    weightSum += weight;
  }
  return { score: Math.round(total / weightSum), modeAvgs };
}

export function scoreClass(s: number): ScoreClass {
  if (s >= 70) return 'good';
  if (s >= 45) return 'mid';
  return 'bad';
}

// ============ Amenity walkability ============
// Walking times to the nearest of each. "Daily" amenities come from Google
// Places; parks and recreation come from OpenStreetMap (Overpass), which has
// real polygons (so parks can be tiered by size) and tags individual courts,
// pitches, pools, and tracks that Google lumps together or omits.
//
// Weights are relative — amenityBlendedScore normalises by the weights of
// whatever the user has enabled, so turning things on/off never breaks the
// 0–100 scale. `defaultOn` is the out-of-the-box selection.
export const AMENITIES: Amenity[] = [
  // ---- daily (Google Places) ----
  { key: 'groceryMajor', label: 'Major grocery', short: 'grocery', group: 'daily', source: 'google', placesType: 'supermarket', match: 'majorChain', mustExist: true, missMsg: 'no major chain in nearest 20', weight: 0.22, good: 8, mid: 18, defaultOn: true },
  { key: 'groceryAny', label: 'Any market', short: 'any market', group: 'daily', source: 'google', placesType: 'supermarket', weight: 0.04, good: 5, mid: 12, defaultOn: true },
  { key: 'transit', label: 'Transit', short: 'transit', group: 'daily', source: 'google', placesType: 'transit_station', weight: 0.18, good: 5, mid: 10, defaultOn: true },
  { key: 'cafe', label: 'Cafe', short: 'cafe', group: 'daily', source: 'google', placesType: 'cafe', weight: 0.12, good: 5, mid: 12, defaultOn: true },
  { key: 'bar', label: 'Bar', short: 'bar', group: 'daily', source: 'google', placesType: 'bar', weight: 0.12, good: 7, mid: 15, defaultOn: true },
  { key: 'convenience', label: 'Corner store', short: 'corner', group: 'daily', source: 'google', placesType: 'convenience_store', weight: 0.08, good: 5, mid: 10, defaultOn: true },
  { key: 'restaurant', label: 'Restaurants', short: 'dining', group: 'daily', source: 'google', placesType: 'restaurant', weight: 0.08, good: 5, mid: 12, defaultOn: true },

  // ---- parks by size (OSM leisure=park polygons) ----
  // Thresholds: 2 ha ≈ 5 acres (a real neighborhood park, e.g. Duboce Park is
  // ~1.6 ha, Alamo Square ~5 ha); 20 ha ≈ 50 acres (somewhere you can run a
  // loop — Dolores is ~6 ha, McLaren ~125 ha, Golden Gate ~410 ha).
  { key: 'parkSmall', label: 'Pocket park', short: 'pocket park', group: 'parks', source: 'osm', osmCategory: 'park', weight: 0.04, good: 5, mid: 12, defaultOn: true },
  { key: 'parkMedium', label: 'Neighborhood park', short: 'nbhd park', group: 'parks', source: 'osm', osmCategory: 'park', minAreaSqm: 20_000, weight: 0.08, good: 8, mid: 15, defaultOn: true },
  { key: 'parkLarge', label: 'Big park', short: 'big park', group: 'parks', source: 'osm', osmCategory: 'park', minAreaSqm: 200_000, mustExist: true, missMsg: 'no big park within 3 km', weight: 0.12, good: 10, mid: 22, defaultOn: true },

  // ---- recreation (OSM) — opt-in ----
  { key: 'tennis', label: 'Tennis courts', short: 'tennis', group: 'recreation', source: 'osm', osmCategory: 'tennis', weight: 0.1, good: 8, mid: 18, defaultOn: false },
  { key: 'pickleball', label: 'Pickleball', short: 'pickleball', group: 'recreation', source: 'osm', osmCategory: 'pickleball', weight: 0.1, good: 8, mid: 18, defaultOn: false },
  { key: 'basketball', label: 'Basketball courts', short: 'hoops', group: 'recreation', source: 'osm', osmCategory: 'basketball', weight: 0.1, good: 8, mid: 18, defaultOn: false },
  { key: 'soccer', label: 'Soccer fields', short: 'soccer', group: 'recreation', source: 'osm', osmCategory: 'soccer', weight: 0.1, good: 10, mid: 20, defaultOn: false },
  { key: 'track', label: 'Running track', short: 'track', group: 'recreation', source: 'osm', osmCategory: 'track', weight: 0.1, good: 10, mid: 20, defaultOn: false },
  { key: 'pool', label: 'Swimming pool', short: 'pool', group: 'recreation', source: 'osm', osmCategory: 'pool', weight: 0.1, good: 10, mid: 20, defaultOn: false },
  { key: 'gym', label: 'Gym', short: 'gym', group: 'recreation', source: 'osm', osmCategory: 'gym', weight: 0.1, good: 8, mid: 15, defaultOn: false },
  { key: 'climbing', label: 'Climbing gym', short: 'climbing', group: 'recreation', source: 'osm', osmCategory: 'climbing', weight: 0.1, good: 12, mid: 25, defaultOn: false },
  { key: 'playground', label: 'Playground', short: 'playground', group: 'recreation', source: 'osm', osmCategory: 'playground', weight: 0.1, good: 5, mid: 12, defaultOn: false },
  { key: 'dogPark', label: 'Dog park', short: 'dog park', group: 'recreation', source: 'osm', osmCategory: 'dog_park', weight: 0.1, good: 8, mid: 15, defaultOn: false },
];

export const AMENITY_GROUP_LABELS: Record<AmenityGroup, string> = {
  daily: 'Daily places',
  parks: 'Parks',
  recreation: 'Recreation & fitness',
};

export const AMENITY_BY_KEY: Record<string, Amenity> = Object.fromEntries(AMENITIES.map((a) => [a.key, a]));
export const DEFAULT_AMENITY_KEYS: string[] = AMENITIES.filter((a) => a.defaultOn).map((a) => a.key);

/**
 * Resolve a saved preference list to known keys. No list at all → defaults;
 * an explicit list (even empty) is honoured, minus keys we no longer define.
 */
export function resolveAmenityKeys(enabled: string[] | null | undefined): Set<string> {
  if (!Array.isArray(enabled)) return new Set(DEFAULT_AMENITY_KEYS);
  return new Set(enabled.filter((k) => k in AMENITY_BY_KEY));
}

/** Amenities that currently count, in canonical order. */
export function activeAmenities(enabled: Set<string> = new Set(DEFAULT_AMENITY_KEYS)): Amenity[] {
  return AMENITIES.filter((a) => enabled.has(a.key));
}

// Optional regional PREFERENCE lists, substring-matched case-insensitively
// against the Google Places name. When a location is in a region these cover,
// a recognized chain/major park is preferred; everywhere else the scorer falls
// back to the nearest prominent result (see computeAmenities), so the app still
// works globally. Extend these per region as desired.
export const MAJOR_GROCERY_CHAINS = [
  // US (incl. SF Bay Area)
  'safeway', 'whole foods', 'trader joe', 'costco', 'lucky', 'target', 'walmart',
  "gus's", 'mollie stone', 'andronico', 'smart & final', 'smart and final',
  'grocery outlet', 'foodsco', 'foods co', 'sprouts', 'kroger', 'albertsons',
  'publix', 'aldi', 'wegmans', 'h-e-b', 'ralphs',
  // UK / EU
  'tesco', 'sainsbury', 'waitrose', 'asda', 'morrisons', 'lidl', 'carrefour',
  'mercadona', 'rewe', 'edeka', 'coop', 'migros',
];

export const MAJOR_PARKS = [
  // SF Bay Area examples; elsewhere the nearest park is used as a fallback.
  'golden gate park', 'presidio', 'crissy field', 'twin peaks',
  'lands end', 'sutro', 'glen canyon', 'mclaren park',
  'lake merced', 'buena vista park', 'bernal heights park', 'mountain lake park',
];

export function isMajorChain(name: string | undefined | null): boolean {
  if (!name) return false;
  const n = name.toLowerCase();
  return MAJOR_GROCERY_CHAINS.some((chain) => n.includes(chain));
}

export function isMajorPark(name: string | undefined | null): boolean {
  if (!name) return false;
  const n = name.toLowerCase();
  return MAJOR_PARKS.some((p) => n.includes(p));
}

export const NAME_MATCHERS: Record<string, (name?: string | null) => boolean> = {
  majorChain: isMajorChain,
  majorPark: isMajorPark,
};

export function amenityScore(minutes: number | null | undefined, amenity: Amenity): number {
  if (minutes == null) return 0;
  const { good, mid } = amenity;
  const bad = mid * 1.6;
  if (minutes <= good) return 100;
  if (minutes <= mid) return 100 - (50 * (minutes - good)) / (mid - good);
  if (minutes <= bad) return 50 - (50 * (minutes - mid)) / (bad - mid);
  return 0;
}

export function amenityBlendedScore(
  candidate: Candidate,
  enabled: Set<string> = new Set(DEFAULT_AMENITY_KEYS),
): number | null {
  if (!candidate.amenities) return null;
  let total = 0;
  let weightSum = 0;
  let foundCount = 0;
  for (const a of activeAmenities(enabled)) {
    const am = candidate.amenities[a.key];
    if (!am) continue;
    if (am.error) {
      // Required amenity is missing — count it as 0, don't just skip.
      if (a.mustExist) weightSum += a.weight;
      continue;
    }
    foundCount++;
    total += amenityScore(am.minutes, a) * a.weight;
    weightSum += a.weight;
  }
  if (foundCount === 0 || weightSum === 0) return null;
  return Math.round(total / weightSum);
}

// ============ Headline ranking weights ============
export const COMMUTE_WEIGHT = 0.55;
export const AMENITY_WEIGHT = 0.35;
export const TRIP_WEIGHT = 0.1;

// Trips (airport, weekend spots, family) are typically driven and often
// regional, so be lenient on time.
export const TRIP_THRESHOLDS = { good: 25, mid: 50 };

// ============ Trips ============
export function bestTripMinutes(tripData: TripLegData | undefined | null): number | null {
  if (!tripData) return null;
  const times: number[] = [];
  if (tripData.driving?.minutes != null) times.push(tripData.driving.minutes);
  if (tripData.transit?.minutes != null) times.push(tripData.transit.minutes);
  return times.length ? Math.min(...times) : null;
}

export function tripScoreFromMinutes(minutes: number | null): number {
  if (minutes == null) return 0;
  const { good, mid } = TRIP_THRESHOLDS;
  const bad = mid * 1.4;
  if (minutes <= good) return 100;
  if (minutes <= mid) return 100 - (50 * (minutes - good)) / (mid - good);
  if (minutes <= bad) return 50 - (50 * (minutes - mid)) / (bad - mid);
  return 0;
}

export function tripScore(tripData: TripLegData | undefined | null): number {
  return tripScoreFromMinutes(bestTripMinutes(tripData));
}

export function tripBlendedScore(candidate: Candidate, trips: Trip[]): number | null {
  if (!candidate.trips || !trips.length) return null;
  let total = 0;
  let count = 0;
  for (const t of trips) {
    const tr = candidate.trips[t.id];
    if (bestTripMinutes(tr) != null) {
      total += tripScore(tr);
      count++;
    }
  }
  if (count === 0) return null;
  return Math.round(total / count);
}

export function tripTimeClass(min: number): ScoreClass {
  if (min <= TRIP_THRESHOLDS.good) return 'good';
  if (min <= TRIP_THRESHOLDS.mid) return 'mid';
  return 'bad';
}

// ============ Overall ============
export interface OverallScore {
  total: number;
  commute: number;
  amenity: number | null;
  trip: number | null;
  modeAvgs: Partial<Record<TravelMode, number>>;
}

export function overallScore(
  candidate: Candidate,
  trips: Trip[],
  enabledAmenities?: Set<string>,
): OverallScore | null {
  const commute = blendedScore(candidate);
  const amenity = amenityBlendedScore(candidate, enabledAmenities);
  const trip = tripBlendedScore(candidate, trips);
  if (!commute) return null;

  let total = commute.score * COMMUTE_WEIGHT;
  let weightUsed = COMMUTE_WEIGHT;
  if (amenity != null) {
    total += amenity * AMENITY_WEIGHT;
    weightUsed += AMENITY_WEIGHT;
  }
  if (trip != null) {
    total += trip * TRIP_WEIGHT;
    weightUsed += TRIP_WEIGHT;
  }
  return {
    total: Math.round(total / weightUsed),
    commute: commute.score,
    amenity,
    trip,
    modeAvgs: commute.modeAvgs,
  };
}

// ============ Palette ============
export const CANDIDATE_COLORS = [
  '#c44536', // sienna red (primary, matches design)
  '#1d3528', // forest green
  '#b8861f', // amber
  '#5a8a3a', // olive
  '#7d3d8e', // muted plum
  '#b35900', // burnt orange
  '#3d5a8e', // muted indigo
  '#76695a', // warm gray
];
